<template>
  <div>
    <Filters
      :filters="usersFilters"
      filter-key="users"
      :initial-data="filtersStatus.users"
      :loading="filtersLoading"
    />

    <TableWrapper
      :table-columns="usersColumns"
      table-key="users"
      row-key="id"
      @editUser="goToEdit"
      @enableUser="enableUser"
      @disableUser="disableUser"
      @massiveAction="bulkApplyRole"
      @unlockUser="unlockUser"
    />

    <EditUser
      :visible="openEditUser"
      :initial-data="initialUserData"
      :roles="roles"
      @dialogClose="handleDialogClose"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import { usersColumns, usersFilters } from '@/config';
  import { usersApi, rolesApi } from '@/api';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import axios from 'axios';

  import EditUser from './EditUser.vue';

  export default {
    name: 'UsersIndex',
    components: {
      TableWrapper,
      Filters,
      EditUser,
    },
    mixins: [abortControllerMixin, filterLoadingMixin],
    data() {
      return {
        usersColumns,
        usersFilters,
        initialUserData: {},
        roles: [],
        openEditUser: false,
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus']),
      // massiveActionsOptions() {
      //   return this.roles.map((r) => ({ label: r, value: r }));
      // },
      // canUseBulkApply() {
      //   return acl.userCanUse(['usersManage']);
      // },
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          const usersIndexResponse = await usersApi.index(
            this.tableFilters('users'),
            this.filtersStatus.users,
            { signal }
          );
          const usersCountResponse = await usersApi.count(
            this.filtersStatus.users,
            { signal }
          );
          const usersWithRoles = usersIndexResponse.data.map((user) => ({
            ...user,
            children:
              user.roles &&
              user.roles.map((role) => ({
                id: role.id + 9999, // again, bad but i need a unique ID key
                role: role.name,
                laboratory: role.laboratory,
                locationId: role.locationId,
                location: role.location ? role.location.name : '',
              })),
          }));
          this.updateTableRows({ tableKey: 'users', rows: usersWithRoles });
          this.updateTableCount({
            tableKey: 'users',
            count: usersCountResponse.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      async enableUser(row) {
        try {
          await usersApi.patch(row.id, { isEnabled: true });
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('users.update.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async disableUser(row) {
        try {
          await usersApi.patch(row.id, { isEnabled: false });
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('users.update.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async getUser() {
        try {
          const user = await usersApi.show(this.$route.params.userId);
          this.initialUserData = { ...user.data };
          this.openEditUser = true;
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
          this.$router.push({ name: 'users' });
        }
      },
      async getRoles() {
        try {
          const roles = await rolesApi.index({});
          this.roles = roles.data.map((r) => r.name);
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async bulkApplyRole(role, selectedRows) {
        try {
          await usersApi.bulkUpdate(
            selectedRows.map((r) => r.id),
            { role }
          );
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('users.update.bulkSuccess'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async unlockUser(row) {
        try {
          await usersApi.unlockUser(row.id);
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('users.update.success'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      goToEdit(row) {
        this.$router.push({
          name: 'usersEdit',
          params: { userId: row.id },
        });
      },
      handleDialogClose(status) {
        this.openEditUser = false;
        if (status.success) this.tableRows();
        this.$router.push({ name: 'users' }).catch(() => {});
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.tableRows();
      this.getRoles();
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        if (to.params.userId) vm.getUser();
      });
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      this.getRoles();
      next();
    },
  };
</script>
